import React from 'react';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiPause, FiTarget, FiAlertTriangle, FiShield, FiClock, FiCheckCircle } = FiIcons;

/**
 * Sanitizes text for Quick Summary display - removes ALL markdown artifacts
 * Handles malformed/unbalanced markdown like word** or **word
 */
const sanitizeSummaryText = (text) => {
  if (!text) return '';
  return text
    // Remove balanced markdown first
    .replace(/\*\*([^*]+)\*\*/g, '$1')    // **bold**
    .replace(/\*([^*]+)\*/g, '$1')         // *italic*
    .replace(/__([^_]+)__/g, '$1')         // __bold__
    .replace(/_([^_]+)_/g, '$1')           // _italic_
    // Remove unbalanced/malformed markdown
    .replace(/\*{1,2}/g, '')               // Any remaining * or **
    .replace(/_{1,2}/g, '')                // Any remaining _ or __
    .replace(/`+/g, '')                    // Backticks
    // Remove headers and list markers
    .replace(/^#{1,6}\s*/gm, '')           // # headers
    .replace(/^\s*[-*+]\s+/gm, '')         // bullet points - * +
    .replace(/^\s*\d+\.\s+/gm, '')         // numbered lists
    // Remove links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Clean up HTML entities and whitespace
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')                  // multiple spaces to single
    .trim();
};

/**
 * Truncates text to max length at word boundary with ellipsis
 * Never cuts mid-word
 */
const truncateAtWord = (text, maxLength = 180) => {
  if (!text || text.length <= maxLength) return text;
  
  // Find the last space before maxLength
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If there's a space, cut there; otherwise just use maxLength
  const cutPoint = lastSpace > maxLength * 0.5 ? lastSpace : maxLength;
  return text.substring(0, cutPoint).trim() + '…';
};

/**
 * Formats invalidation level as actionable sentence
 */
const formatInvalidation = (invalidation, bias) => {
  if (!invalidation) return '';
  
  // Check if it's just a number/price
  const priceMatch = invalidation.match(/^[\d,]+(?:\.\d+)?$/);
  if (priceMatch) {
    const direction = bias === 'LONG' ? 'below' : bias === 'SHORT' ? 'above' : 'past';
    return `Invalid if breaks ${direction} ${invalidation}`;
  }
  
  // Already a sentence
  return invalidation;
};

/**
 * Parses AI analysis text and extracts structured decision data
 * Returns status, bias, confidence, primaryReason, invalidation, reviewTrigger, keyLevels
 */
export const parseAnalysisDecision = (text) => {
  if (!text || typeof text !== 'string') {
    return { status: 'ERROR', errorCode: 'NO_TEXT', uiMessage: 'No analysis text received' };
  }

  // ✅ FIRST: Check if response contains structured analysis fields
  // If it has these, it's NOT a refusal even if it contains disclaimer language
  const hasStructuredContent = 
    /(?:Direction|Entry|Stop Loss|TP[123]|Take Profit|Support|Resistance|Key Level)[\s:]/i.test(text) ||
    /(?:TRADE_AUTHORIZED|TRADE_APPROVED|WAIT|NO_TRADE)/i.test(text) ||
    /(?:bullish|bearish)\s+(?:bias|structure|trend)/i.test(text) ||
    /\$[0-9,]+(?:\.[0-9]+)?/i.test(text); // Contains price levels

  // ✅ REFUSAL DETECTION - Only if NO structured content AND contains refusal phrases
  const refusalPatterns = [
    /unable to provide real-?time/i,
    /can'?t give trading signals/i,
    /cannot provide (?:real-?time|trading|investment)/i,
    /as an AI,?\s*I\s*(?:cannot|can'?t|am unable)/i,
    /I'?m sorry,?\s*I\s*(?:cannot|can'?t)/i,
    /I cannot provide (?:financial|trading|investment) advice/i,
    /not (?:able|permitted) to (?:provide|give|offer)/i,
    /beyond (?:my|the) (?:scope|capabilities)/i,
    /I don'?t have access to (?:real-?time|live|current) (?:market )?data/i
  ];

  // Only trigger error if NO structured content AND has refusal pattern
  if (!hasStructuredContent) {
    for (const pattern of refusalPatterns) {
      if (pattern.test(text)) {
        return {
          status: 'ERROR',
          errorCode: 'MODEL_REFUSAL',
          uiMessage: "We couldn't read this chart clearly. Please upload a higher quality screenshot with visible timeframe, price scale, and recent candles."
        };
      }
    }
  }

  // Check for very short/empty responses that indicate failure
  if (text.length < 100 && !hasStructuredContent) {
    return {
      status: 'ERROR',
      errorCode: 'INSUFFICIENT_RESPONSE',
      uiMessage: 'The analysis was too short. Please try uploading a clearer chart image.'
    };
  }

  // ✅ PARSE DECISION STATUS
  let status = 'WATCH'; // Default to WATCH if no clear decision
  let bias = 'NEUTRAL';
  let confidence = 0;
  let primaryReason = '';
  let invalidation = '';
  let reviewTrigger = '';

  // ✅ AUTHORITATIVE: extract Decision from the structured EXECUTION_DECISION_BLOCK first.
  // The model is instructed to emit a machine-readable block whose Decision: line
  // is the single source of truth. Loose narrative phrases (section headings like
  // "Final Trade Authorization", or "TRADE AUTHORIZED" appearing in scenario lists)
  // are NOT verdicts and must not override.
  const decisionBlockMatch = text.match(
    /EXECUTION_DECISION_BLOCK[\s\S]*?Decision\*{0,2}[:\s]*\*{0,2}\s*([A-Z_\- ]+?)(?:\r?\n|━|$)/i
  );
  if (decisionBlockMatch) {
    const raw = decisionBlockMatch[1].trim().toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
    if (/^WAIT|WATCH$/.test(raw) || raw.startsWith('WAIT')) status = 'WAIT';
    else if (/^NO_?TRADE/.test(raw)) status = 'NO_TRADE';
    else if (/TRADE_?(?:AUTHORIZED|APPROVED)/.test(raw) || raw === 'TRADE') status = 'AUTHORIZED';
    // else leave NEUTRAL
  }

  // Fallback: only if the structured block is absent, use fuzzy patterns.
  // Order matters — check WAIT/NO_TRADE BEFORE AUTHORIZED so a "Final Trade
  // Authorization" section heading doesn't override an explicit WAIT decision.
  if (status === 'NEUTRAL') {
    const waitPatterns = [
      /⏳\s*WAIT/i,
      /WAIT\s*[—–\-−]+\s*(?:STRUCTURE|CONDITIONS)/i,
      /Decision\*{0,2}[:\s]*\*{0,2}\s*WAIT/i,
      /status[:\s]+["']?WAIT["']?/i,
      /\bWAIT\s*[-–—]\s*CONDITIONS\s*NOT\s*MET/i,
    ];
    for (const p of waitPatterns) {
      if (p.test(text)) { status = 'WAIT'; break; }
    }
  }

  if (status === 'NEUTRAL') {
    const noTradePatterns = [
      /❌\s*NO-?TRADE/i,
      /NO-?TRADE\s*[—–\-−]+/i,
      /Decision\*{0,2}[:\s]*\*{0,2}\s*NO_?TRADE/i,
      /status[:\s]+["']?NO_?TRADE["']?/i,
    ];
    for (const p of noTradePatterns) {
      if (p.test(text)) { status = 'NO_TRADE'; break; }
    }
  }

  if (status === 'NEUTRAL') {
    // Only ACTIONABLE phrasings — not section headings.
    const authorizedPatterns = [
      /✅\s*\*{0,2}\s*TRADE\s*AUTHORIZED/i,
      /Decision\*{0,2}[:\s]*\*{0,2}\s*TRADE_?(?:AUTHORIZED|APPROVED)/i,
      /TRADE\s*AUTHORIZED\s*[—–\-−]+\s*(?:LONG|SHORT)/i,
      /status[:\s]+["']?AUTHORIZED["']?/i,
    ];
    for (const p of authorizedPatterns) {
      if (p.test(text)) { status = 'AUTHORIZED'; break; }
    }
  }

  // ✅ PARSE BIAS (LONG/SHORT/NEUTRAL) - Priority: structured block > trade setup > narrative.
  // The EXECUTION_DECISION_BLOCK is the AI's authoritative answer. It can explicitly say
  // Direction: NEUTRAL / NONE / N/A (e.g. on WAIT decisions). We must respect that and NOT
  // fall through to grab a LONG/SHORT from a "conditional preparation" setup further down,
  // which would mislabel a WAIT decision as LONG.
  // 1. EXECUTION_DECISION_BLOCK (highest priority — accepts NEUTRAL/NONE/N/A too)
  const execBlockMatch = text.match(/EXECUTION_DECISION_BLOCK[\s\S]*?Direction\*{0,2}[:\s]*\*{0,2}\s*(LONG|SHORT|NEUTRAL|NONE|N\/A)/i);
  // 2. Trade Setup / Final Authorization section direction
  const tradeSetupMatch = text.match(/(?:Trade\s*(?:Setup|Decision)|Final\s*Trade\s*Authorization)[\s\S]{0,300}?Direction\*{0,2}[:\s]*\*{0,2}\s*(LONG|SHORT)/i);
  // 3. Standalone "Direction:" line (closest to structured data)
  const directionLineMatch = text.match(/^\s*\*{0,2}Direction\*{0,2}[:\s]*\*{0,2}\s*(LONG|SHORT)/im);
  // 4. "TRADE AUTHORIZED – SHORT/LONG" pattern
  const authDirectionMatch = text.match(/TRADE\s*AUTHORIZED\s*[—–\-−]+\s*(LONG|SHORT)/i);

  // If the structured block matched at all, trust it — even if it said NEUTRAL/NONE/N/A.
  // This is the AI's authoritative answer; do NOT fall through to other matches.
  if (execBlockMatch) {
    const raw = execBlockMatch[1].toUpperCase();
    bias = (raw === 'LONG' || raw === 'SHORT') ? raw : 'NEUTRAL';
  } else {
    const directionSource = tradeSetupMatch || authDirectionMatch || directionLineMatch;
    if (directionSource) {
      bias = directionSource[1].toUpperCase();
    } else if (/LONG\s*(?:bias|position|setup)/i.test(text) && !/SHORT\s*(?:bias|position|setup)/i.test(text)) {
      bias = 'LONG';
    } else if (/SHORT\s*(?:bias|position|setup)/i.test(text) && !/LONG\s*(?:bias|position|setup)/i.test(text)) {
      bias = 'SHORT';
    } else if (/bearish/i.test(text) && !/bullish/i.test(text)) {
      bias = 'SHORT';
    } else if (/bullish/i.test(text) && !/bearish/i.test(text)) {
      bias = 'LONG';
    }
  }

  // ✅ FINAL SAFETY NET: when the decision is WAIT or NO_TRADE, force NEUTRAL bias.
  // Those analyses commonly include "Conditional Bullish/Bearish Setup — Preparation Only"
  // sections with Direction: LONG/SHORT that the parser would otherwise grab, producing
  // a misleading LONG/SHORT badge next to a WAIT decision.
  if (status === 'WAIT' || status === 'NO_TRADE') {
    bias = 'NEUTRAL';
  }

  // ✅ PARSE CONFIDENCE — prefer EXECUTION_DECISION_BLOCK source of truth.
  // The narrative often contains scenario probabilities (e.g. "BULLISH: ... probability 35%")
  // that aren't the AI's actual confidence in its decision. The structured block has the
  // canonical value.
  const confidenceBlockMatch = text.match(
    /EXECUTION_DECISION_BLOCK[\s\S]*?Confidence\*{0,2}[:\s]*\*{0,2}\s*(\d+)/i
  );
  if (confidenceBlockMatch) {
    confidence = parseInt(confidenceBlockMatch[1], 10);
  } else {
    // Fallback: most specific phrasings first to avoid matching scenario probabilities
    const candidates = [
      /Setup\s*Confidence[:\s*]*(\d+)/i,
      /Probability\s*of\s*Success[:\s*]*(\d+)/i,
      /Setup\s*Quality[:\s*]*(\d+)/i,
      /(?:^|\n)\s*Confidence[:\s*]*(\d+)/i,
    ];
    for (const re of candidates) {
      const m = text.match(re);
      if (m) { confidence = parseInt(m[1], 10); break; }
    }
  }

  // ✅ PARSE PRIMARY REASON - handles GPT + Gemini formats
  // NOTE: order matters — more specific patterns first
  const reasonPatterns = [
    /Primary[_\s]?Reason\*{0,2}[:\s]*\*{0,2}\s*([^\n]+)/i,
    /\*\*Rationale\*\*[:\s]*\*{0,2}\s*([^\n]+)/i,
    /Rationale[:\s]+([^\n]+)/i,
    /Entry\s*Condition\*{0,2}[:\s]*\*{0,2}\s*([^\n]{15,})/i,        // Gemini: "Entry Condition:"
    /\bReason\s*:\s*([^\n]+)/i,
    /Key\s*(?:Finding|Observation)\*{0,2}[:\s]*\*{0,2}\s*([^\n]+)/i
  ];
  // Gemini: block patterns where reason is on next line(s)
  const blockPatterns = [
    /\*{0,2}Reasoning\*{0,2}[:\s]*\n+\s*[-*•]?\s*([^\n]{15,})/i,
    /\*{0,2}Logic\*{0,2}[:\s]+\*{0,2}\s*([^\n]{15,})/i,
    /\*{0,2}Justification\*{0,2}[:\s]*\n+\s*[-*•\d.]*\s*\*{0,2}([^\n]{15,})/i,
    /\*{0,2}Entry\s*Condition\*{0,2}[:\s]*\n+\s*([^\n]{15,})/i,         // Gemini: "Entry Condition:\n sentence"
    /\*{0,2}Entry\s*Condition\*{0,2}[:\s]+\*{0,2}\s*([^\n]{15,})/i,     // Gemini inline
    /\*{0,2}Execution\s*Bias\*{0,2}[:\s]+\*{0,2}\s*([^\n]{15,})/i,     // "Execution Bias:"
  ];
  for (const bp of blockPatterns) {
    if (primaryReason) break;
    const m = text.match(bp);
    if (m) {
      const cleaned = sanitizeSummaryText(m[1]);
      // Skip if it's just a label value like "Standard" or "BUY LIMIT"
      if (cleaned.length > 20) {
        primaryReason = truncateAtWord(cleaned, 180);
      }
    }
  }
  if (!primaryReason) {
    for (const pattern of reasonPatterns) {
      const match = text.match(pattern);
      if (match) {
        const candidate = match[1].trim();
        if (!/^[━=\-#*]+$/.test(candidate) && !/^[A-Z\s&\/]+$/.test(candidate) && candidate.length > 10) {
          primaryReason = truncateAtWord(sanitizeSummaryText(candidate), 180);
          break;
        }
      }
    }
  }

  // Fallback: extract first meaningful sentence from narrative text (skip headers/decorators)
  if (!primaryReason) {
    // Split into lines, skip section headers and decorators
    const lines = text.split('\n').filter(line => {
      const trimmed = line.trim();
      return trimmed.length > 30 && 
             !/^[━=\-#*✅❌⏳🔍🧭🌊⚖️🛡️📊🎲]+/.test(trimmed) && // skip decorator lines
             !/^\*{0,2}[0-9]+\./.test(trimmed) && // skip numbered sections
             !/^[A-Z\s&\/()]{10,}$/.test(trimmed) && // skip ALL CAPS headers
             /[a-z]/.test(trimmed); // must have lowercase (real sentence)
    });
    for (const line of lines) {
      const cleaned = sanitizeSummaryText(line).trim();
      // Must start with uppercase letter or number (not a fragment like "implied by volume)")
      if (cleaned.length > 30 && /^[A-Z0-9]/.test(cleaned) && 
          /(?:structure|support|resistance|trend|momentum|price|level|zone|market|trading|bullish|bearish|consolidat|breakout|flag|impuls)/i.test(cleaned)) {
        primaryReason = truncateAtWord(cleaned, 180);
        break;
      }
    }
  }

  // ✅ PARSE INVALIDATION - Look for concrete price levels (Gemini + GPT formats)
  // Priority: price-based invalidation first, then sentence-based
  const invalidationPricePat = /(?:Invalid(?:ation)?(?:\s*(?:Level|if|:))?)[:\s]*(?:.*?)\$?([\d,]+(?:\.\d+)?)/i;
  // Handles "INVALIDATION RULES:" followed by bullet point on next line
  const invalidationRulesBulletPat = /INVALIDATION\s*RULES?[:\s]*\n+\s*[-*•]?\s*\*{0,2}(?:Price\s*Action|Invalidation)?[:\s]*\*{0,2}\s*([^\n]{15,120})/i;
  // Gemini: "**Invalidation Level:** A 1H candle close below **$5,150**."
  const invalidationLevelPat = /\*{0,2}Invalidation\s*Level\*{0,2}[:\s]*\*{0,2}\s*([^\n]{10,120})/i;
  const invalidationSentPat = /(?:\*\*)?Invalidation(?:\*\*)?[:\s]*([^\n*#]{15,120})/i;
  const closePat = /(?:(?:1H|4H|1D)\s*candle\s*closes?|closes?\s*(?:above|below))\s*\*{0,2}\$?([\d,]+(?:\.\d+)?)/i;
  // Fallback: look for any "clear X candle close above/below $PRICE invalidates" sentence
  const invalidateSentencePat = /(?:invalidates?|invalidation)[^.]*?\$?([\d,]+(?:\.\d+)?)/i;
  
  const invalidationCandidates = [
    invalidationLevelPat,
    invalidationPricePat,
    invalidationRulesBulletPat,
    closePat,
    invalidationSentPat,
    invalidateSentencePat,
  ];

  for (const pattern of invalidationCandidates) {
    const match = text.match(pattern);
    if (match) {
      let extracted = (match[1] || '').trim();
      extracted = sanitizeSummaryText(extracted).trim();
      // Must be meaningful: at least a price or 10+ chars, not a fragment
      const hasPrice = /\$?[\d,]+(?:\.\d+)?/.test(extracted);
      const isFragment = extracted.length < 8;
      const isHeader = /^(?:No-?Trade|Conditions?|&|\*|#)/i.test(extracted);
      if (!isFragment && !isHeader && (hasPrice || extracted.length >= 15)) {
        // If it's just a number, format it nicely
        if (/^[\d,]+(?:\.\d+)?$/.test(extracted)) {
          const direction = bias === 'LONG' ? 'below' : bias === 'SHORT' ? 'above' : 'past';
          invalidation = `Breaks ${direction} $${extracted}`;
        } else {
          invalidation = truncateAtWord(extracted, 100);
        }
        break;
      }
    }
  }

  // ✅ PARSE REVIEW TRIGGER
  const reviewMatch = text.match(/(?:Review[_\s]?Trigger|Re-?evaluate\s*(?:if|when))[:\s]*([^\n]+)/i);
  if (reviewMatch) {
    reviewTrigger = truncateAtWord(sanitizeSummaryText(reviewMatch[1]), 100);
  }

  // Clean up primary reason - already sanitized+truncated at extraction point
  const primaryReasonFull = sanitizeSummaryText(primaryReason);
  const primaryReasonDisplay = primaryReason; // already word-truncated

  return {
    status,
    bias,
    confidence,
    primaryReason: primaryReasonDisplay,
    primaryReasonFull,
    invalidation,
    reviewTrigger,
    keyLevels: [],
    uiMessage: '',
    errorCode: ''
  };
};

/**
 * Quick Summary Card - Always shown at top of analysis results
 * Displays decision status (AUTHORIZED/WAIT/NO_TRADE/WATCH/ERROR)
 */
const AnalysisQuickSummary = ({ analysisText, symbol = 'Asset' }) => {
  const decision = parseAnalysisDecision(analysisText);

  // Get status styling - Dashboard style with bg-gray-900/60 + colored borders
  const getStatusConfig = () => {
    switch (decision.status) {
      case 'AUTHORIZED':
        return {
          icon: FiCheckCircle,
          label: 'TRADE AUTHORIZED',
          borderClass: 'border-emerald-500/30',
          textClass: 'text-emerald-400',
          iconClass: 'text-emerald-400',
          badgeBg: 'bg-emerald-500/20'
        };
      case 'WAIT':
        return {
          icon: FiClock,
          label: 'WAIT - STRUCTURE NOT CONFIRMED',
          borderClass: 'border-purple-500/30',
          textClass: 'text-purple-400',
          iconClass: 'text-purple-400',
          badgeBg: 'bg-purple-500/20'
        };
      case 'NO_TRADE':
        return {
          icon: FiAlertTriangle,
          label: 'NO TRADE - CONDITIONS NOT MET',
          borderClass: 'border-orange-500/30',
          textClass: 'text-orange-400',
          iconClass: 'text-orange-400',
          badgeBg: 'bg-orange-500/20'
        };
      case 'ERROR':
        return {
          icon: FiAlertTriangle,
          label: 'ANALYSIS ERROR',
          borderClass: 'border-gray-500/30',
          textClass: 'text-gray-400',
          iconClass: 'text-gray-400',
          badgeBg: 'bg-gray-500/20'
        };
      default: // WATCH
        return {
          icon: FiTarget,
          label: 'WATCH - MONITORING',
          borderClass: 'border-cyan-500/30',
          textClass: 'text-cyan-400',
          iconClass: 'text-cyan-400',
          badgeBg: 'bg-cyan-500/20'
        };
    }
  };

  const config = getStatusConfig();

  // If error status, don't show summary (ChartReadabilityError will handle it)
  if (decision.status === 'ERROR') {
    return null;
  }

  return (
    <div className={`relative overflow-hidden rounded-xl border ${config.borderClass} bg-gray-900/60 backdrop-blur-sm`}>
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent pointer-events-none" />
      
      <div className="relative p-4">
        {/* Status Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${config.badgeBg} border ${config.borderClass} flex items-center justify-center`}>
              <SafeIcon icon={config.icon} className={`w-5 h-5 ${config.iconClass}`} />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Quick Summary</div>
              <div className={`text-sm font-bold ${config.textClass}`}>{config.label}</div>
            </div>
          </div>
          
          {/* Bias Badge */}
          {decision.bias !== 'NEUTRAL' && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
              decision.bias === 'LONG' 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              <SafeIcon 
                icon={decision.bias === 'LONG' ? FiTrendingUp : FiTrendingDown} 
                className="w-3.5 h-3.5" 
              />
              <span>{decision.bias}</span>
            </div>
          )}
        </div>

        {/* Key Info Grid */}
        <div className="grid grid-cols-2 gap-2">
          {/* Primary Reason */}
          {decision.primaryReason && (
            <div 
              className="col-span-2 bg-black/20 rounded-lg p-3 border border-white/5 cursor-default"
              title={decision.primaryReasonFull || decision.primaryReason}
            >
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Primary Reason</div>
              <div className="text-xs text-gray-300 leading-relaxed line-clamp-3">
                {decision.primaryReason}
              </div>
            </div>
          )}

          {/* Confidence */}
          {decision.confidence > 0 && (
            <div className="bg-black/20 rounded-lg p-2.5 border border-white/5">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Confidence</div>
              <div className={`text-sm font-bold ${
                decision.confidence >= 70 ? 'text-emerald-400' : 
                decision.confidence >= 50 ? 'text-amber-400' : 'text-red-400'
              }`}>{decision.confidence}%</div>
            </div>
          )}

          {/* Review Trigger */}
          {decision.reviewTrigger && (
            <div className="bg-black/20 rounded-lg p-2.5 border border-white/5">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Review Trigger</div>
              <div className="text-xs text-gray-400">{decision.reviewTrigger}</div>
            </div>
          )}

          {/* Invalidation */}
          {decision.invalidation && (
            <div className="col-span-2 bg-black/20 rounded-lg p-2.5 border border-red-500/10">
              <div className="text-[10px] text-red-400/80 uppercase tracking-wider mb-0.5">Invalidation</div>
              <div className="text-xs text-red-300">{formatInvalidation(decision.invalidation, decision.bias)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisQuickSummary;
